/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {express, lowLevel} from 'phenix-web-sdk';
import config from './config';

let channelExpress;
let channelService;
let publisher;

export const start = (onAuthError) => {
  const expressOptions = {onError: onAuthError || console.error};

  if (config.cdn.replacement) {
    expressOptions.streamingSourceMapping = {
      patternToReplace: config.cdn.pattern,
      replacement: config.cdn.replacement
    };
  }

  if (config.auth.token) {
    expressOptions.authToken = config.auth.token;
  }

  if (config.useShaka) {
    expressOptions.shakaLoader = (callback) => {
      import(/* webpackChunkName: "shaka-player" */ 'shaka-player').then(module => {
        const shaka = module.default;

        if (!config.environment.canPlayHls && !shaka.Player.isBrowserSupported()) {
          shaka.polyfill.installAll();
        }

        callback(shaka);
      }).catch(e => {
        console.error('Failed to load shaka', e);

        callback(null);
      });
    };
  } else {
    expressOptions.webPlayerLoader = (callback) => {
      import(/* webpackChunkName: "phenix-web-player" */ 'phenix-web-player').then(module => {
        const webPlayer = module.default;

        callback(webPlayer);
      }).catch(e => {
        console.error('Failed to load phenix web player', e);

        callback(null);
      });
    };
  }

  channelExpress = new express.ChannelExpress(expressOptions);

  const logger = channelExpress.getPCastExpress().getPCast().getLogger();

  window.onerror = function(e) {
    logger.error('Window Error', e);
  };

  if (config.cdn.replacement) {
    logger.info('[CHANNEL] Using provided CDN for all live streaming [%s], replacing [%s]', config.cdn.replacement, config.cdn.pattern);
  }
};

export const joinChannel = (videoElement, onStreamChanged) => {
  const bandwidthToStartAt = config.environment.isMobile ? 700000 : 3000000;
  const subscriberOptions = {bandwidthToStartAt: bandwidthToStartAt};

  if (config.subscriber.targetLatency) {
    subscriberOptions.targetLatency = config.subscriber.targetLatency;
  }

  if ((config.environment.isIOS || config.environment.isSafari) && config.subscriber.hlsTargetDuration) {
    subscriberOptions.hlsTargetDuration = config.subscriber.hlsTargetDuration;
  }

  if (config.audio.disableAutoMuting) {
    subscriberOptions.disableAutoMuting = true;
  }

  if (config.subscriber.tags) {
    subscriberOptions.tags = config.subscriber.tags;
  }

  const joinChannelOptions = {
    videoElement: videoElement,
    streamSelectionStrategy: config.selectionStrategy || 'most-recent',
    subscriberOptions: subscriberOptions,
    token: config.subscriber.token
  };
  const channelId = config.subscriber.channelId;

  return new Promise((resolve, reject) => {
    return channelExpress.joinChannel(joinChannelOptions, (error, response) => {
      if (error) {
        reject(error);

        notifyParent({
          channelId,
          action: 'joinChannel',
          type: 'joinChannelCallback',
          error: error.message || 'Unhandled error'
        });

        return;
      }

      notifyParent({
        channelId,
        action: 'joinChannel',
        type: 'joinChannelCallback',
        status: response.status
      });

      if (response.status !== 'ok') {
        reject(new Error(response.status));

        return;
      }

      if (response.channelService) {
        channelService = response.channelService;
      }

      resolve();
    }, (error, response) => {
      if (error) {
        onStreamChanged(error);

        notifyParent({
          channelId,
          action: 'joinChannel',
          type: 'subscriberCallback',
          error: error.message || 'Unhandled error'
        });

        return;
      }

      notifyParent({
        channelId,
        action: 'joinChannel',
        type: 'subscriberCallback',
        status: response.status
      });

      if (response.status === 'offline') {
        return;
      }

      onStreamChanged(null, response);
    });
  });
};

export const leaveChannel = () => {
  if (!channelService) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    channelService.leaveChannel((error, response) => {
      const channelId = config.subscriber.channelId || config.publisher.channelId;

      if (error) {
        reject(error);

        notifyParent({
          channelId,
          action: 'leaveChannel',
          type: 'leaveChannelCallback',
          error: error.message || 'Unhandled error'
        });

        return;
      }

      notifyParent({
        channelId,
        action: 'leaveChannel',
        type: 'leaveChannelCallback',
        status: response.status
      });

      channelService = null;

      if (response.status !== 'ok') {
        reject(new Error(`Unable to leave channel, ${response.status}`));

        return;
      }

      resolve();
    });
  });
};

export const publishToChannel = (userMedia, onEnd) => {
  const connectOptions = [];
  const publishToChannelOptions = {
    token: config.publisher.token,
    userMediaStream: userMedia,
    frameRate: null,
    connectOptions: connectOptions,
    viewerStreamSelectionStrategy: config.selectionStrategy || 'most-recent',
    screenName: `WebChannelPublisher-${new Date().toISOString().replace(/[^0-9|^T]/g, '').slice(0, -3)}`
  };

  return new Promise((resolve) => {
    return channelExpress.publishToChannel(publishToChannelOptions, (error, response) => {
      if (error) {
        return onEnd(error);
      }

      if (response.status !== 'ok') {
        return onEnd(null, response);
      }

      if (response.channelService) {
        channelService = response.channelService;
      }

      publisher = response.publisher;
      resolve(response.publisher);
    });
  });
};

export const stopPublisher = () => {
  if (!publisher) {
    return;
  }

  publisher.stop();
};

export const getChannelInfo = () => {
  if (!channelExpress) {
    return;
  }

  const channelId = config.publisher.channelId;
  const pcastExpress = channelExpress.getPCastExpress();

  return new Promise((resolve, reject) => {
    pcastExpress.waitForOnline(() => {
      const pcast = pcastExpress ? pcastExpress.getPCast() : null;
      const tempRoomService = new lowLevel.RoomService(pcast);

      return tempRoomService.getRoomInfo(channelId, null, (error, response) => {
        if (error) {
          return reject(error);
        }

        const room = response ? response.room : null;
        const channel = room ? room.toJson() : {};

        channel.channelId = channel.roomId;

        delete channel.roomId;

        return resolve(channel);
      });
    });
  });
};

export const stop = () => {
  return channelExpress.stop();
};

function notifyParent(message) {
  if (window.parent.postMessage) {
    window.parent.postMessage(message, '*');
  }
}